import { useEffect, useMemo, useState } from 'react';
import {
  fetchCaseSheetConsents, fetchCaseSheetEvidence, fetchClinicalTodayWork,
  type CaseHistoryEntry, type CaseSheetClinicalView, type CaseSheetConsentsWire, type ClinicalEvidenceItem,
  type ClinicalTodayWorkView, type ConsultPatientBanner, type PatientClinicalProfileView,
} from '@/dashboard/api';
import type { Patient360AppointmentWire } from '@/dashboard/patients/patient-360-view';
import { AcButton } from '@/dashboard/ui';
import { clinicToday } from '@/dashboard/ui/day-scope';
import { EvidenceFullView } from './ClinicalEvidencePanel';
import { buildPatientRecord, RECORD_CARDS, type RecordRow, type RecordTab } from './consult-patient-record-view';

type SheetReads = {
  key: string;
  todayWork: Record<number, ClinicalTodayWorkView | null>;
  consents: Record<number, CaseSheetConsentsWire | null>;
  evidence: Record<number, readonly ClinicalEvidenceItem[] | null>;
  failed: boolean;
};

/** Frozen V14g Patient record inside the consult. Saved records only, read
 * through the doctor's existing scoped readers; payments are never shown. */
export function ConsultPatientRecord({ patient, viewerSpecialty, profile, sheets, sheetsState, history, doctorName, effectiveStatus,
  appointments, returnLine, unsaved, attachmentsEnabled, clinicId, onBack }: {
  patient: ConsultPatientBanner; viewerSpecialty: string | null; profile: PatientClinicalProfileView | null;
  sheets: readonly CaseSheetClinicalView[]; sheetsState: 'loading' | 'ready' | 'error'; history: readonly CaseHistoryEntry[];
  doctorName: (id: number | null | undefined) => string | null; effectiveStatus?: (owningSheetId: number, itemId: number) => string | null;
  appointments: readonly Patient360AppointmentWire[] | null; returnLine: string | null; unsaved: boolean;
  attachmentsEnabled: boolean; clinicId: number; onBack: () => void;
}): JSX.Element {
  const ids = [...new Set(sheets.map(sheet => sheet.id))].sort((a, b) => a - b);
  const key = `${patient.patientId}:${ids.join(',')}:${attachmentsEnabled}`;
  const [attempt, setAttempt] = useState(0);
  const [reads, setReads] = useState<SheetReads | null>(null);
  const [tab, setTab] = useState<RecordTab>('Dental');
  const [expanded, setExpanded] = useState<readonly string[]>([]);
  const [opened, setOpened] = useState<ClinicalEvidenceItem | null>(null);
  useEffect(() => {
    let cancelled = false;
    setReads(null);
    void Promise.all(ids.map(async id => {
      const [work, consents, evidence] = await Promise.all([
        fetchClinicalTodayWork(id).then(value => value.caseSheetId === id ? value : null, () => null),
        fetchCaseSheetConsents(id).catch(() => null),
        attachmentsEnabled ? fetchCaseSheetEvidence(id).then(list => list.items.filter(item => item.caseSheetId === id), () => null) : Promise.resolve([] as const),
      ]);
      return { id, work, consents, evidence };
    })).then(rows => {
      if (cancelled) return;
      setReads({
        key,
        todayWork: Object.fromEntries(rows.map(row => [row.id, row.work])),
        consents: Object.fromEntries(rows.map(row => [row.id, row.consents])),
        evidence: Object.fromEntries(rows.map(row => [row.id, row.evidence])),
        failed: rows.some(row => row.work === null || row.consents === null || row.evidence === null),
      });
    });
    return () => { cancelled = true; };
    // ids are summarised by key.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key, attempt]);
  const current = reads?.key === key ? reads : null;
  const model = useMemo(() => buildPatientRecord({
    today: clinicToday(), viewerSpecialty, sheets, history, doctorName, effectiveStatus,
    todayWork: current?.todayWork ?? {}, consents: current?.consents ?? {}, evidence: current?.evidence ?? {},
    appointments, profile, returnLine,
  }), [viewerSpecialty, sheets, history, doctorName, effectiveStatus, current, appointments, profile, returnLine]);
  const active: RecordTab = model.tabs.includes(tab) ? tab : 'Dental';
  const age = patient.ageYears !== null ? String(patient.ageYears) : null;
  const sex = patient.gender ? patient.gender.slice(0, 1).toUpperCase() : null;
  const tiles: readonly [string, string, boolean?][] = [
    ['Phone', 'Masked for doctors'],
    ['Allergies', model.tiles.allergies.text, model.tiles.allergies.alert],
    ['Active plans', model.tiles.activePlans],
    ['Last visit', model.tiles.lastVisit],
    ['Return visit', model.tiles.returnVisit],
    ['History confirmed', model.tiles.historyConfirmed],
  ];
  const rowView = (row: RecordRow) => <div className="consult-record__row" key={row.key}>
    <span className="consult-record__date">{row.date}</span>
    <span className="consult-record__main">{row.main}</span>
    <span className="consult-record__side">{row.side}</span>
    {row.open ? <AcButton variant="ghost" size="sm" onClick={() => setOpened(row.open!.item)}>Open</AcButton> : <span aria-hidden="true" />}
  </div>;
  const specialtyCards = active === 'Timeline' ? [] : RECORD_CARDS.filter(card => (model.cards[active][card] ?? []).length > 0);
  return <div className="dashboard consult-workspace consult-workspace--v14g"><section className="consult-record" data-testid="consult-record" aria-label="Patient record">
    <div className="consult-record__head">
      <div><AcButton variant="ghost" onClick={onBack} data-testid="consult-record-back">← Back to consultation</AcButton></div>
      <h1>{patient.patientName ?? 'Patient record'} <span>{[patient.umr, [age, sex].filter(Boolean).join(' ')].filter(Boolean).join(' · ')}</span></h1>
      <div className="consult-record__tiles">{tiles.map(([label, value, alert]) => <div key={label} className={alert ? 'is-alert' : undefined} data-testid={`consult-record-tile-${label.toLowerCase().replace(/\s+/g, '-')}`}><span>{label}</span><b>{value}</b></div>)}</div>
    </div>
    {unsaved ? <p className="consult-record__draft" role="status">This visit has changes that are not saved yet. The record shows saved information only.</p> : null}
    <div className="consult-record__tabs" role="tablist" aria-label="Record sections">
      {model.tabs.map(name => <AcButton key={name} variant="ghost" role="tab" aria-selected={active === name} onClick={() => setTab(name)}>{name}</AcButton>)}
    </div>
    <div role="tabpanel" aria-label={active}>
      {sheetsState === 'loading' || !current ? <p role="status" className="consult-record__status">Loading the saved record…</p> : null}
      {sheetsState === 'error' || current?.failed ? <div role="status" className="consult-record__status"><p>Some saved details could not be loaded. Shown items are saved; nothing is filled in.</p>
        <AcButton variant="secondary" size="sm" onClick={() => setAttempt(value => value + 1)}>Retry</AcButton></div> : null}
      {active === 'Timeline' ? <div className="consult-record__timeline" data-testid="consult-record-timeline">
        {model.timeline.length ? model.timeline.map(item => <div className="consult-record__tl" key={`${item.card}:${item.key}`}>
          <span className="consult-record__date">{item.date}</span><span className={`consult-record__dot is-${item.specialty.toLowerCase()}`} aria-hidden="true" />
          <div><b>{item.main}</b><div className="consult-record__side">{item.specialty} · {item.card} · {item.side}</div></div>
          {item.open ? <AcButton variant="ghost" size="sm" onClick={() => setOpened(item.open!.item)}>Open</AcButton> : <span aria-hidden="true" />}
        </div>) : <p className="consult-record__empty">No history yet.</p>}
      </div> : specialtyCards.length ? <div className="consult-record__cards">{specialtyCards.map(card => {
        const rows = model.cards[active][card] ?? [];
        const all = expanded.includes(card);
        return <section className="consult-record__card" key={card} data-testid={`consult-record-card-${card.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
          <header><h3>{card}</h3><span className="consult-record__count">{rows.length}</span></header>
          {rows.slice(0, all ? rows.length : 3).map(row => rowView(row))}
          {rows.length > 3 ? <footer><AcButton variant="ghost" size="sm" onClick={() => setExpanded(list => all ? list.filter(name => name !== card) : [...list, card])}>{all ? 'Show fewer' : `View all ${rows.length}`}</AcButton></footer> : null}
        </section>;
      })}</div> : <p className="consult-record__empty">No {active} visits for this patient.</p>}
    </div>
    {model.unrecordedArea ? <p className="consult-record__scope">Visits without a recorded area are listed under Dental.</p> : null}
    <p className="consult-record__scope">You see the categories your role already allows. Payments are shown to Reception and Accounts, not here.</p>
    {opened ? <EvidenceFullView item={opened} clinicId={opened.clinicId ?? clinicId} onClose={() => setOpened(null)} /> : null}
  </section></div>;
}
